import { AppIcon } from '@backrs_ui/common-ui';

import {
  SearchParentCategory,
  SearchCategory,
} from '@backrs_ui/grow-ui/src/pages/Search/search.types';

import { Box, Typography, Popover, MenuItem, SxProps, Theme } from '@mui/material';

import { filterSlug, handleSubMenuClick, optionSlug } from './filterDropdown.util';

type SecondLevelPopoverProps = Readonly<{
  anchorEl: HTMLElement | null;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  selectedParentKey: SearchCategory;
  menuItems: SearchParentCategory['categories'];
  selectedItems: SearchCategory[];
  onSelect: (newItems: SearchCategory[]) => void;
  setSelectedParentKey: (selectedParentKey: SearchCategory | null) => void;
  name: string;
}>;

export const SecondLevelPopover = ({
  anchorEl,
  setAnchorEl,
  selectedParentKey,
  menuItems,
  selectedItems,
  onSelect,
  setSelectedParentKey,
  name,
}: SecondLevelPopoverProps) => {
  const selectSlug = filterSlug(name);

  const currentDropdownKeys: SearchCategory[] = menuItems.flatMap((item) => {
    const subItems = item.subCategories?.map((subItem) => subItem.key);

    return [item.key, ...(subItems || [])];
  });

  const selectedItemsInThisDropdown = selectedItems.filter((item) =>
    currentDropdownKeys.includes(item),
  );

  const selectedItemsFromOtherDropdowns = selectedItems.filter(
    (item) => !currentDropdownKeys.includes(item),
  );

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={() => {
        setAnchorEl(null);
        setSelectedParentKey(null);
      }}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      PaperProps={{ sx: menuPaperPropsPopoverSx }}
    >
      {menuItems
        .filter((item) => item.key === selectedParentKey)
        .map((item) =>
          item.subCategories?.map((subItem) => (
            <MenuItem
              key={subItem.key}
              id={`filter-${selectSlug}-option-${optionSlug(subItem.key)}`}
              value={subItem.key}
              sx={menuItemStyle}
              onClick={() => {
                handleSubMenuClick({
                  subItem,
                  selectedItems,
                  onSelect,
                  menuItems,
                  firstLayerMenuItems: menuItems,
                  selectedItemsInThisDropdown,
                  selectedItemsFromOtherDropdowns,
                });
              }}
            >
              <Box sx={menuItemContentStyle}>
                <Box
                  id={`filter-${selectSlug}-option-${optionSlug(subItem.key)}-checkbox`}
                  sx={menuItemIconStyle(selectedItems?.includes(subItem.key))}
                >
                  <AppIcon
                    iconName={selectedItems?.includes(subItem.key) ? 'Check' : 'Square'}
                    diameter="16px"
                    color={
                      selectedItems?.includes(subItem.key)
                        ? 'Colors/Foreground/fg-white'
                        : 'currentColor'
                    }
                  />
                </Box>
                <Typography variant="text-md-medium">{subItem.label}</Typography>
              </Box>
            </MenuItem>
          )),
        )}
    </Popover>
  );
};

const menuItemStyle: SxProps<Theme> = ({ backrs }) => ({
  '&.Mui-selected': {
    backgroundColor: backrs.colors['Colors/Base/Transparent'],
    '&:hover': {
      backgroundColor: backrs.colors['Colors/Base/Transparent'],
    },
  },
  '&.MuiMenuItem-root:hover': {
    backgroundColor: 'transparent !important',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent !important',
  },
});

const menuItemContentStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const menuItemIconStyle =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    flexShrink: 0,
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const menuPaperPropsPopoverSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-md'],
  maxHeight: '420px',
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  ml: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-md'],
});
